import { Key, useCallback, useEffect, useState } from "react";
import { useApi } from "../../../hooks/useApi";
import validationSchemaFromFields from "../helpers/validationSchemaFromFields";

const flattenFields = (fields: any[]): any[] =>
    fields.reduce((carry, field) => {
        if (field.type === "section") {
            return [...carry, ...(field.children ?? []).flatMap((child) => flattenFields(child.fields ?? []))];
        }

        return [...carry, field];
    }, []);

const useFetchFields = (formId: Key) => {
    const api = useApi();
    const [fields, setFields] = useState([]);
    const [form, setForm] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const renderFieldFilters = useCallback((field: any) => {
        const maxNameLength = 100;
        const fieldLabel = typeof field.label === 'string' && field.label.trim().length > 0 ? field.label : field.name;
        const filter = {
            filterName:
                "Formular: " +
                (fieldLabel.length > maxNameLength ? fieldLabel.slice(0, maxNameLength) + "..." : fieldLabel),
            filterKey: "form_data_" + field.name,
            enableInverted: false,
        };

        // if (field.name === "trainingplan_birthdate") return { ...filter, filterType: "date" };
        // if (field.name === "trainingplan_age") return { ...filter, filterType: "numeric" };

        switch (field.type) {
            case "select":
                return {
                    ...filter,
                    filterType: "select",
                    options: field.options
                        ?.filter((option) => typeof option === "string" && option.trim().length > 0)
                        .map((option) => ({ label: option, value: option })),
                };

            case "payment_options":
                return {
                    ...filter,
                    filterType: "select",
                    options: field.options
                        ?.filter((option) => typeof option?.option === "string" && option.option.trim().length > 0)
                        .map((option) => ({ label: option.option, value: option.price })),
                };

            case "checkbox":
                if (field.options?.length > 0) {
                    return {
                        ...filter,
                        filterType: "select",
                        options: field.options
                            ?.filter((option) => typeof option === "string" && option.trim().length > 0)
                            .map((option) => ({ label: option, value: option })),
                    };
                }
                return {
                    ...filter,
                    filterType: "select",
                    options: [
                        { label: "Ja", value: "true" },
                        { label: "Nein", value: "false" },
                    ],
                };

            default:
                return {
                    ...filter,
                    filterType: "text",
                };
        }
    }, []);

    const fetchFields = () => {
        if (Number.isSafeInteger(formId))
            void api({
                url: `form/${formId}`,
                onSuccess: ({ data: { fields, ...form } }) => {
                    setForm(form);
                    setFields(fields);
                    setIsLoading(false);
                },
            });
        else setIsLoading(false);
    };

    useEffect(fetchFields, [formId]);

    return {
        form,
        fields,
        filters: flattenFields(fields)
            .filter((field) => !["wysiwyg", "table", "section"].includes(field.type))
            .map(renderFieldFilters),
        isLoading,
        validationSchema: validationSchemaFromFields(fields),
        setForm,
        setFields,
        setIsLoading,
    };
};

export default useFetchFields;
